import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useParcels } from "@/hooks/useParcels";
import { Parcel } from "@/mocks/parcels";
import AdminLogin from "./components/AdminLogin";
import ParcelModal from "./components/ParcelModal";
import StatsBar from "./components/StatsBar";
import StatusBadge from "@/pages/tracking/components/StatusBadge";

const LOGO_URL = "https://static.readdy.ai/image/76abf0d92622af9fc4827e62ca023833/9bd36fccc1442f12a415c76af596da08.jpeg";

export default function AdminPage() {
  const { t } = useTranslation();
  const { parcels, loading, addParcel, updateParcel, deleteParcel } = useParcels();
  const [authed, setAuthed] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [modal, setModal] = useState<{ open: boolean; mode: "add" | "edit"; parcel?: Parcel | null }>({
    open: false,
    mode: "add",
    parcel: null,
  });
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    if (sessionStorage.getItem("admin_auth") === "true") {
      setAuthed(true);
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("admin_auth");
    setAuthed(false);
  };

  const filtered = parcels.filter(
    (p) =>
      p.id.toLowerCase().includes(search.toLowerCase()) ||
      p.recipient.toLowerCase().includes(search.toLowerCase()) ||
      p.currentLocation.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async (parcel: Parcel) => {
    setSaving(true);
    setSaveError(null);
    try {
      if (modal.mode === "add") {
        await addParcel(parcel);
      } else {
        await updateParcel(parcel.id, parcel);
      }
      setModal({ open: false, mode: "add", parcel: null });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save parcel");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      await deleteParcel(id);
      setDeleteConfirm(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to delete parcel");
    } finally {
      setSaving(false);
    }
  };

  if (!authed) {
    return <AdminLogin onLogin={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-800 text-white flex-col fixed top-0 left-0 bottom-0 z-40 hidden lg:flex">
        <div className="px-5 py-5 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg overflow-hidden">
              <img src={LOGO_URL} alt="Logo" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="font-bold text-sm">{t("nav_brand")}</div>
              <div className="text-slate-400 text-xs">{t("admin_title")}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {[
            { icon: "ri-dashboard-line", label: t("admin_title"), active: true },
            { icon: "ri-box-3-line", label: t("nav_tracking"), active: false },
          ].map((item) => (
            <div
              key={item.label}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${
                item.active
                  ? "bg-emerald-600 text-white"
                  : "text-slate-400 hover:bg-slate-700 hover:text-white"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={item.icon} />
              </div>
              {item.label}
            </div>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-slate-700">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-slate-700 hover:text-white transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-home-line" />
            </div>
            {t("nav_home")}
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-slate-700 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-line" />
            </div>
            {t("admin_logout")}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-100 px-4 md:px-6 h-16 flex items-center justify-between sticky top-0 z-30">
          <div>
            <h1 className="font-bold text-slate-800 text-lg">{t("admin_title")}</h1>
            <p className="text-slate-400 text-xs hidden sm:block">{t("admin_subtitle")}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleLogout}
              className="lg:hidden flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-logout-box-line" />
              </div>
              {t("admin_logout")}
            </button>
            <button
              onClick={() => setModal({ open: true, mode: "add", parcel: null })}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line" />
              </div>
              {t("admin_add_parcel")}
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 md:p-6">
          <StatsBar parcels={parcels} />

          {/* Search + Table */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative w-full sm:w-72">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                  <i className="ri-search-line text-sm" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("admin_search_placeholder")}
                  className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 text-sm outline-none focus:border-emerald-400 transition-colors"
                />
              </div>
              <span className="text-xs text-slate-400">{filtered.length} parcels</span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-gray-100">
                    {[
                      t("admin_col_tracking"),
                      t("admin_col_recipient"),
                      t("admin_col_status"),
                      t("admin_col_location"),
                      t("admin_col_updated"),
                      t("admin_col_actions"),
                    ].map((col) => (
                      <th key={col} className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12">
                        <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-loader-4-line animate-spin" />
                          </div>
                          Loading parcels...
                        </div>
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-slate-400 text-sm">
                        {t("admin_no_parcels")}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((parcel) => (
                      <tr key={parcel.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-5 py-3.5">
                          <Link
                            to={`/tracking/${parcel.id}`}
                            className="font-mono text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer"
                          >
                            {parcel.id}
                          </Link>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-700">{parcel.recipient}</td>
                        <td className="px-5 py-3.5">
                          <StatusBadge status={parcel.status} size="sm" />
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5 text-sm text-slate-600">
                            <div className="w-4 h-4 flex items-center justify-center shrink-0">
                              <i className="ri-map-pin-line text-slate-400 text-xs" />
                            </div>
                            <span className="truncate max-w-[160px]">{parcel.currentLocation}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-xs text-slate-400 whitespace-nowrap">{parcel.lastUpdated}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setModal({ open: true, mode: "edit", parcel })}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-emerald-100 text-slate-500 hover:text-emerald-600 transition-colors cursor-pointer"
                              title={t("admin_edit")}
                            >
                              <i className="ri-edit-line text-sm" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(parcel.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-500 transition-colors cursor-pointer"
                              title={t("admin_delete")}
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* Parcel Modal */}
      {modal.open && (
        <ParcelModal
          mode={modal.mode}
          parcel={modal.parcel}
          onSave={handleSave}
          onClose={() => setModal({ open: false, mode: "add", parcel: null })}
          saving={saving}
          saveError={saveError}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="font-bold text-slate-800 text-center mb-2">{t("admin_delete")}</h3>
            <p className="text-slate-500 text-sm text-center mb-6">{t("admin_confirm_delete")}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={saving}
                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-slate-600 text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {t("admin_cancel")}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={saving}
                className="flex-1 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && (
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin" />
                  </div>
                )}
                {t("admin_delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
